import { PageContainer } from '@components/Layout/PageContainer';
import { PageTitle } from '@components/Layout/PageTitle';
import { mobileBottomNavigationHeight } from '@modules/application/components/MobileBottomNavigation/MobileBottomNavigation';
import { TaskActions } from '../components/Actions/TaskActions';
import { TaskFormDrawer } from '../components/TaskFormDrawer';
import { TaskListItem } from '../components/TaskListItem';
import { TaskStatusDisplay } from '../components/TaskStatusDisplay';
import AddIcon from '@mui/icons-material/Add';
import { TaskAlt } from '@mui/icons-material';
import { Box, Button, CircularProgress, Fab, LinearProgress, List, Stack } from '@mui/material';
import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { TasksPermission } from 'gql/index';
import { TasksFiltersProvider, useTasksFilters } from './hooks/useTasksFilters';
import { AllTasksFilters } from './components/AllTasksFilters';
import { useResponsive } from '@utils/useResponsive';

const AllTasksPageContent: React.FC = () => {
  const { formatMessage } = useIntl();
  const {
    tasks,
    isFetching,
    isFetchingNextPage,
    isLoading,
    hasNextPage,
    fetchNextPage
  } = useTasksFilters();

  const { isMobile } = useResponsive();
  const [selectedTask, setSelectedTask] = useState<{ taskId?: number; projectId?: number } | null>(null);

  const handleOpenTask = (taskId: number, projectId: number) => {
    setSelectedTask({ taskId, projectId });
  };

  const handleCloseDrawer = () => setSelectedTask(null);

  const handleCreateClick = () => {
    setSelectedTask({ projectId: undefined });
  };

  const isEmpty = !isLoading && tasks.length === 0;

  return (
    <>
    <PageTitle
      backgroundColor='secondary'
      title={formatMessage({ id: 'Tasks' })}
      icon={<TaskAlt />}
      loading={isFetching}
    />
    <PageContainer>
      <Stack gap={2}>
        <AllTasksFilters />

        {(isLoading || isFetching) && (
          <LinearProgress />
        )}

        {isEmpty && (
          <Box display='flex' alignItems='center' justifyContent='center' py={4}>
            {formatMessage({ id: 'There are no tasks to show.' })}
          </Box>
        )}

        {!isEmpty && (
          <List disablePadding >
            {tasks.map(task => (
              <TaskListItem
                key={task.id}
                task={task}
                onOpenTask={(task, projectId) => handleOpenTask(task.id, projectId)}
                secondaryAction={
                  <Stack direction='row' alignItems='center' spacing={1}>
                    {!isMobile && <TaskStatusDisplay status={task.status} />}
                    <TaskActions task={task} />
                  </Stack>  
                }
              />
            ))}
          </List>
        )}

        {hasNextPage && (
          <Button
            onClick={() => fetchNextPage()}
            disabled={isFetchingNextPage}
            variant='outlined'
          >
            {isFetchingNextPage
              ? <CircularProgress size={16} />
              : formatMessage({ id: 'Load more' })}
          </Button>
        )}
      </Stack>

      {selectedTask && (
        <TaskFormDrawer
          taskId={selectedTask?.taskId}
          projectId={selectedTask?.projectId}
          enableProjectSelection={!selectedTask.taskId} // no selected task means we can add a task to any project
          projectFilter={(project) =>
            !project.isTemplate && project.myPermissions?.tasksPermission === TasksPermission.Manage && !project.isArchived
          }
          open={selectedTask != null}
          onClose={handleCloseDrawer}
        />
      )}

      <Fab
        color='primary'
        aria-label={formatMessage({ id: 'Add task' })}
        onClick={handleCreateClick}
        sx={{
          position: 'fixed',
          bottom: (theme) =>
            `calc(${theme.spacing(3)} + ${isMobile ? `${mobileBottomNavigationHeight}px` : '0px'})`,
          right: (theme) => theme.spacing(3),
          zIndex: (theme) => theme.zIndex.appBar + 1
        }}
      >
        <AddIcon />
      </Fab>
    </PageContainer>
    </>
  );
};

export const AllTasksPage: React.FC = () => (
  <TasksFiltersProvider>
    <AllTasksPageContent />
  </TasksFiltersProvider>
);
